import type { TodayTodoItem } from '@fdh/shared';
import { APPROVAL_STATUS, TASK_KIND, type ApprovalStatus } from '@fdh/shared';
import { EmployerHistoryStatusIcon } from './EmployerHistoryStatusIcon';
import { EmployerStatusRibbon } from './EmployerStatusRibbon';
import { ListNavRow } from '../../ui/ListNavRow';
import { extractEmployerTaskCompletionDisplay } from '../../../utils/employerTaskCompletionDisplay';

const getEmployerHistoryStatusLabel = (
  task: TodayTodoItem,
  isAwaitingSubmission: boolean,
  t: (key: string, params?: Record<string, string | number>) => string,
): string => {
  if (isAwaitingSubmission) return t('task.status.notSubmitted');
  const map: Record<ApprovalStatus, string> = {
    [APPROVAL_STATUS.PENDING]: t('task.status.pending'),
    [APPROVAL_STATUS.APPROVED]: t('task.status.approved'),
    [APPROVAL_STATUS.REJECTED]: t('task.status.rejected'),
    [APPROVAL_STATUS.NEEDS_UPDATE]: t('task.status.needsUpdate'),
    [APPROVAL_STATUS.CANCEL]: t('task.status.cancel'),
  };
  return map[task.status];
};

type EmployerHistoryTaskRowProps = {
  task: TodayTodoItem;
  onViewDetail: (taskInstanceId: string) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

export const EmployerHistoryTaskRow = ({ task, onViewDetail, t }: EmployerHistoryTaskRowProps) => {
  const isAwaitingSubmission =
    task.status === APPROVAL_STATUS.PENDING &&
    (!task.submittedAt || String(task.submittedAt).trim() === '');

  const completionDisplay = extractEmployerTaskCompletionDisplay(task.completedAt ?? '', task.dueDate);

  const statusAria = getEmployerHistoryStatusLabel(task, isAwaitingSubmission, t);

  const titleForAriaLabel =
    task.taskKind === TASK_KIND.INVENTORY
      ? `${task.title} (${t('task.list.kindStockTake')})`
      : task.title;

  return (
    <li>
      <ListNavRow
        onClick={() => onViewDetail(task.taskInstanceId)}
        ariaLabel={`${titleForAriaLabel} — ${statusAria}`}
        ribbon={<EmployerStatusRibbon status={task.status} submittedAt={task.submittedAt ?? ''} />}
      >
        <div className="flex items-center gap-3">
          <EmployerHistoryStatusIcon status={task.status} isAwaitingSubmission={isAwaitingSubmission} />
          <div className="min-w-0 flex-1 pr-10">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">{task.title}</h3>
              {task.taskKind === TASK_KIND.INVENTORY ? (
                <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                  {t('task.list.kindStockTake')}
                </span>
              ) : null}
            </div>
            {completionDisplay ? (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('task.history.completionTime', { time: completionDisplay })}
              </p>
            ) : null}
          </div>
        </div>
      </ListNavRow>
    </li>
  );
};

export { submittedAtSortKey } from '../../../utils/employerTaskCompletionDisplay';
